/**
 * `wayai seed …` — named fixtures that make a preview base a hermetic eval
 * target.
 *
 * Fixtures are authored as config-as-code (`wayai-ws/bases/<base>/seeds/<name>.yaml`)
 * and pushed; these verbs then drive the STORED fixture by name, so none of them
 * carries a body. Preview-only, enforced server-side — a fixture stamps a
 * reserved ownership marker so reset/clear revert exactly its own rows.
 *
 * Top-level: `wayai status` (auth) and `wayai seed lease
 * status` (this lease) differ by depth, not by word.
 */

import { Command } from 'commander';
import { createDataClient } from '../client.js';
import { printOutput } from '../output.js';
import { outputFormat, pathSegment, requireBase } from '../helpers.js';

export function buildSeedCommand(): Command {
  const seed = new Command('seed').description(
    'Apply, reset, or clear a seed fixture on a preview base (hermetic eval data)',
  );

  seed
    .command('apply <name>')
    .description(
      'Idempotently upsert the fixture (keyed by external_id, so re-applying never twins). Preview-only.',
    )
    .action(async function (this: Command, name: string) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(
        await client.request('POST', `/v1/${base}/seeds/${pathSegment(name, 'fixture name')}/apply`),
        outputFormat(this),
      );
    });

  seed
    .command('reset <name>')
    .description(
      'Restore the declared baseline: re-apply in place (fixing trigger-mutated records) and prune owned rows the fixture no longer declares. Record types listed in exclusive_record_types are treated as authoritative — reset also removes their non-declared records (agent-created residue) and any relationships referencing them. Preview-only.',
    )
    .action(async function (this: Command, name: string) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(
        await client.request('POST', `/v1/${base}/seeds/${pathSegment(name, 'fixture name')}/reset`),
        outputFormat(this),
      );
    });

  seed
    .command('clear <name>')
    .description('Delete every row the fixture owns (teardown). Preview-only.')
    .action(async function (this: Command, name: string) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(
        await client.request('POST', `/v1/${base}/seeds/${pathSegment(name, 'fixture name')}/clear`),
        outputFormat(this),
      );
    });

  seed
    .command('list')
    .description('List the seed fixtures defined on a base')
    .action(async function (this: Command) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(await client.request('GET', `/v1/${base}/seeds`), outputFormat(this));
    });

  seed
    .command('get <name>')
    .description('Show a seed fixture definition (its declared records and relationships)')
    .action(async function (this: Command, name: string) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(await client.request('GET', `/v1/${base}/seeds/${pathSegment(name, 'fixture name')}`), outputFormat(this));
    });

  // Leases are a runtime coordination plane, deliberately OUTSIDE `/seeds/:name/*`
  // so a fixture literally named "lease" stays an ordinary fixture. The server
  // binds authority to the authenticated actor; `owner_ref` is descriptive only,
  // and the immutable tuple must be repeated to release.
  const lease = seed.command('lease').description('Coordinate an isolated fixture lease');

  lease
    .command('acquire <fixture>')
    .description('Reset a fixture and atomically acquire the preview base for one eval run')
    .requiredOption('--lease-id <uuidv7>', 'Client-generated UUIDv7 (permanently single-use)')
    .requiredOption(
      '--owner-ref <ref>',
      'Safe ID: 1-200 letters/digits or ._:@/+~=- (descriptive only)',
    )
    .action(async function (
      this: Command,
      fixture: string,
      opts: { leaseId: string; ownerRef: string },
    ) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(
        await client.request('POST', `/v1/${base}/seed-leases/acquire`, {
          lease_id: opts.leaseId,
          fixture,
          owner_ref: opts.ownerRef,
        }),
        outputFormat(this),
      );
    });

  lease
    .command('status')
    .description('Inspect your lease, or learn only whether another actor has locked the base')
    .option('--lease-id <uuidv7>', 'Inspect a specific lease ID')
    .action(async function (this: Command, opts: { leaseId?: string }) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      const params = new URLSearchParams();
      if (opts.leaseId !== undefined) params.set('lease_id', opts.leaseId);
      const query = params.size > 0 ? `?${params.toString()}` : '';
      printOutput(
        await client.request('GET', `/v1/${base}/seed-leases/status${query}`),
        outputFormat(this),
      );
    });

  lease
    .command('release <fixture>')
    .description('Clear fixture-owned data and release the matching lease')
    .requiredOption('--lease-id <uuidv7>', 'The UUIDv7 used to acquire the lease')
    .requiredOption('--owner-ref <ref>', 'Same safe ID: 1-200 letters/digits or ._:@/+~=-')
    .action(async function (
      this: Command,
      fixture: string,
      opts: { leaseId: string; ownerRef: string },
    ) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(
        await client.request('POST', `/v1/${base}/seed-leases/release`, {
          lease_id: opts.leaseId,
          fixture,
          owner_ref: opts.ownerRef,
        }),
        outputFormat(this),
      );
    });

  return seed;
}
